import { ChangeEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { layout } from '../NewHire/v2/styles';
import AnnualReviewService from '../../../services/AnnualReview';
import { AnnualReviewResponse } from './types';

interface GrowthPlanModalProps {
  review: AnnualReviewResponse;
  token: string;
  onClose: () => void;
  onSaved: (updated: AnnualReviewResponse) => void;
}

const GrowthPlanModal = ({ review, token, onClose, onSaved }: GrowthPlanModalProps) => {
  const [professionalGoal, setProfessionalGoal] = useState(review.growthPlan?.professionalGoal || '');
  const [personalGoal, setPersonalGoal] = useState(review.growthPlan?.personalGoal || '');
  const [actionStep1, setActionStep1] = useState(review.growthPlan?.actionSteps?.[0] || '');
  const [actionStep2, setActionStep2] = useState(review.growthPlan?.actionSteps?.[1] || '');
  const [leadershipSupport, setLeadershipSupport] = useState(review.growthPlan?.leadershipSupport || '');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = professionalGoal.trim().length > 0;

  const submit = async () => {
    if (!canSubmit) {
      toast.error('Please fill in the Professional Goal.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await AnnualReviewService(token).submitGrowthPlan(review.id, {
        growthPlan: {
          professionalGoal: professionalGoal.trim(),
          personalGoal: personalGoal.trim() || undefined,
          actionSteps: [actionStep1.trim(), actionStep2.trim()].filter((s) => s.length > 0),
          leadershipSupport: leadershipSupport.trim() || undefined,
        },
      });
      toast.success('Growth Plan saved.');
      onSaved(res.data);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not save Growth Plan');
    } finally {
      setSubmitting(false);
    }
  };

  const inputProps = layout.input as any;
  const labelProps = layout.label as any;

  return (
    <div>
      <h1 style={{ ...layout.title, marginBottom: 4 }}>Growth Plan — {review.cycleYear}</h1>
      <p style={layout.subtitle as any}>
        The Growth Plan is optional. Fill it out for {review.employeeName} now or later, then regenerate the report to
        include it. Only the Professional Goal is required to save.
      </p>

      <div style={{ marginTop: 20 }}>
        <label style={labelProps}>Professional Goal (Work-Based)</label>
        <textarea
          value={professionalGoal}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setProfessionalGoal(e.target.value)}
          rows={3}
          style={{ ...inputProps, resize: 'vertical', fontFamily: 'inherit' }}
        />

        <label style={{ ...labelProps, marginTop: 14 }}>Personal Goal (optional)</label>
        <textarea
          value={personalGoal}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setPersonalGoal(e.target.value)}
          rows={3}
          style={{ ...inputProps, resize: 'vertical', fontFamily: 'inherit' }}
        />

        <label style={{ ...labelProps, marginTop: 14 }}>Action Step 1</label>
        <input
          type="text"
          value={actionStep1}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setActionStep1(e.target.value)}
          style={inputProps}
        />

        <label style={{ ...labelProps, marginTop: 14 }}>Action Step 2</label>
        <input
          type="text"
          value={actionStep2}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setActionStep2(e.target.value)}
          style={inputProps}
        />

        <label style={{ ...labelProps, marginTop: 14 }}>How Leadership Will Support You</label>
        <textarea
          value={leadershipSupport}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setLeadershipSupport(e.target.value)}
          rows={3}
          style={{ ...inputProps, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24, gap: 12 }}>
        <button type="button" onClick={onClose} style={layout.secondaryBtn}>
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !canSubmit}
          style={canSubmit && !submitting ? layout.primaryBtn : layout.disabledBtn}
        >
          {submitting ? 'Saving…' : 'Save Growth Plan'}
        </button>
      </div>
    </div>
  );
};

export default GrowthPlanModal;
